import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.scss";
import reportWebVitals from "./reportWebVitals";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ConfigProvider, Empty, ThemeConfig } from "antd";

import {
  SignUp,
  Login,
  PasswordRecovery,
  Logout,
  PageNotFound,
  VerifyCode,
} from "auth/pages";
import { Dashboard } from "dashboard/pages";
import { ProtectedRoute } from "auth/components";
import { Settings } from "users/pages";
import { Projects, ProjectDetail, AddProject } from "projects/pages";
import { Forbidden } from "auth/pages/forbidden/Forbidden";
import { UserAdmin } from "admin/pages";
import { DashboardSelector } from "dashboard/components";
import { queryClient } from "shared/api/queryClient";
import { Organizations, OrganizationsLoader, OrganizationSettings } from "organizations/pages";
import { OrganizationLayout, TenantLayout } from "shared/components";
import EligibilityCheckerRoute from "eligibility/pages/EligibilityCheckerRoute";

import variables from "./_variables.scss";
import Auth0ProviderWithRouter from "auth0/Auth0ProviderWithRouter";

// routing configurations
export const router = createBrowserRouter([
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "*",
    element: <PageNotFound />,
  },
  {
    path: "/forbidden",
    element: <Forbidden />,
  },
  {
    path: "/signup",
    element: <SignUp />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/logout",
    element: <Logout />,
  },
  {
    path: "/recovery/password",
    element: <PasswordRecovery />,
  },
  {
    path: "/:tenantId/organizations/:organizationId",
    element: <ProtectedRoute ComponentRoute={OrganizationLayout} />,
    children: [
      {
        path: "dashboard",
        element: <ProtectedRoute ComponentRoute={Dashboard} />,
        children: [
          {
            path: "",
            element: <DashboardSelector />,
          },
        ],
      },
      {
        path: "projects",
        element: (
          <ProtectedRoute
            ComponentRoute={Projects}
            // permissionsNeededToAccessRoute={["project_get"]}
          />
        ),
      },
      {
        path: "projects/new",
        element: (
          <ProtectedRoute
            ComponentRoute={AddProject}
            permissionsNeededToAccessRoute={["create:projects"]}
          />
        ),
      },
      {
        path: "projects/:projectId",
        element: (
          <ProtectedRoute
            ComponentRoute={ProjectDetail}
            // permissionsNeededToAccessRoute={["project_get"]}
          />
        ),
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute
            ComponentRoute={OrganizationSettings}
            permissionsNeededToAccessRoute={["update:organizations"]}
          />
        ),
      },
      {
        path: "eligibility",
        element: (
          <ProtectedRoute
            ComponentRoute={EligibilityCheckerRoute}
            permissionsNeededToAccessRoute={["read:eligibility_checks"]}
          />
        ),
      },
    ],
  },
  {
    path: "/:tenantId",
    element: <ProtectedRoute ComponentRoute={TenantLayout} />,
    children: [
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "admin",
        element: <UserAdmin />,
      },
    ],
  },
  {
    path: "/:tenantId/organizations",
    element: <ProtectedRoute ComponentRoute={OrganizationsLoader} />,
  },
  {
    path: "/verify",
    element: <VerifyCode />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});


const config: ThemeConfig = {
  token: {
    colorPrimary: variables.greenColor,
    colorInfo: variables.greenColor,
    wireframe: false,
    colorLink: variables.blueColor,
    borderRadius: variables.borderRadius,
    fontFamily: "Poppins, sans-serif",
    fontSize: 14,
  },
  components: {
    Input: {
      controlHeightLG: 64,
    },
    Button: {
      controlHeightLG: 48,
    },
    Layout: {
      lightSiderBg: variables.lightGreyColor,
    },
    Menu: {
      itemBg: variables.lightGreyColor,
      itemColor: variables.darkGreyColor,
      itemSelectedBg: variables.greenColor,
      itemSelectedColor: variables.whiteColor,
      itemHoverBg: variables.lightGreenColor,
      itemHoverColor: variables.mediumGreenColor,
    },
    Table: {
      headerBg: variables.whiteColor,
      headerColor: "#425166",
      borderColor: "#d8d8d8",
      footerBg: variables.whiteColor,
    },
    Modal: {
      titleFontSize: 24,
    },
    Breadcrumb: {
      iconFontSize: 16,
    },
  },
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <QueryClientProvider client={queryClient}>
    <Auth0ProviderWithRouter>
      <ConfigProvider theme={config} renderEmpty={() => <Empty />}>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </ConfigProvider>
    </Auth0ProviderWithRouter>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
